import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthContext } from '@/contexts/AuthContext';
import { useWhatsAppChannel } from '@/hooks/useWhatsAppChannel';
import { useWhatsAppHealth } from '@/hooks/useWhatsAppHealth';
import { useConnectWhatsApp } from '@/hooks/useConnectWhatsApp';
import { useDisconnectWhatsApp } from '@/hooks/useDisconnectWhatsApp';

interface SignupData {
  phoneNumberId: string;
  wabaId: string;
}

const WHATSAPP_GREEN = '#25D366';

export function WhatsAppConnectCard() {
  const { t } = useTranslation();
  const { organizationId } = useAuthContext();
  const { data: channel, isLoading } = useWhatsAppChannel(organizationId ?? undefined);
  const health = useWhatsAppHealth(!!channel);
  const connectMutation = useConnectWhatsApp();
  const disconnectMutation = useDisconnectWhatsApp();

  const [sdkReady, setSdkReady] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const signupDataRef = useRef<SignupData | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.FB) {
      setSdkReady(true);
      return;
    }
    if (document.getElementById('facebook-jssdk')) return;

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.FB?.init({
        appId: import.meta.env.VITE_META_APP_ID as string,
        cookie: true,
        xfbml: false,
        version: 'v23.0',
      });
      setSdkReady(true);
    };
    script.onerror = () => toast.error(t('settings.whatsapp.sdkLoadError'));
    document.body.appendChild(script);
  }, [t]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      )
        return;
      try {
        const data = JSON.parse(event.data as string) as {
          type?: string;
          event?: string;
          data?: { phone_number_id: string; waba_id: string };
        };
        if (data.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH' && data.data) {
          signupDataRef.current = {
            phoneNumberId: data.data.phone_number_id,
            wabaId: data.data.waba_id,
          };
        }
      } catch {
        /* not JSON, ignore */
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  function handleConnect() {
    signupDataRef.current = null;
    if (!window.FB) {
      toast.error(t('settings.whatsapp.sdkLoadError'));
      return;
    }
    window.FB.login(
      (response) => {
        if (!response.authResponse?.code) {
          toast.info(t('settings.whatsapp.cancelled'));
          return;
        }
        const data = signupDataRef.current;
        if (!data) {
          toast.error(t('settings.whatsapp.noSignupData'));
          return;
        }
        connectMutation.mutate({
          code: response.authResponse.code,
          metaPhoneNumberId: data.phoneNumberId,
          metaWabaId: data.wabaId,
        });
      },
      {
        config_id: import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID as string,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          feature: 'whatsapp_embedded_signup',
          sessionInfoVersion: '3',
        },
      },
    );
  }

  function handleConfirmDisconnect() {
    setConfirmOpen(false);
    disconnectMutation.mutate();
  }

  if (isLoading) {
    return (
      <div data-testid="whatsapp-card-loading" className="ow-card">
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="ow-card">
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-base font-bold tracking-tight">
            <span style={{ color: WHATSAPP_GREEN }}>WhatsApp</span>{' '}
            {t('settings.whatsapp.title').replace(/whatsapp/i, '').trim() ||
              'Business'}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('settings.whatsapp.description')}
          </p>
        </div>
        <div className="px-6 pb-6 flex flex-col gap-4">
          <div>
            <button
              type="button"
              className="ow-btn ow-btn--primary"
              onClick={handleConnect}
              disabled={!sdkReady || connectMutation.isPending}
            >
              {connectMutation.isPending && (
                <Loader2 size={14} className="animate-spin" />
              )}
              {connectMutation.isPending
                ? t('settings.whatsapp.connecting')
                : t('settings.whatsapp.connect')}
            </button>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-foreground mt-0.5 shrink-0" />
            <span>{t('settings.whatsapp.betaWarning')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ow-card">
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-base font-bold tracking-tight">
            <span style={{ color: WHATSAPP_GREEN }}>WhatsApp</span>{' '}
            {t('settings.whatsapp.title').replace(/whatsapp/i, '').trim() ||
              'Business'}
          </h3>
        </div>

        <div className="px-6 pb-5 flex flex-col gap-4">
          <dl className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">
                {t('settings.whatsapp.phoneLabel')}
              </dt>
              <dd className="font-medium mono">
                {health.data?.displayPhoneNumber ?? channel.phoneNumber}
              </dd>
            </div>
            {channel.displayName && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">
                  {t('settings.whatsapp.wabaLabel')}
                </dt>
                <dd className="font-medium">
                  {health.data?.verifiedName ?? channel.displayName}
                </dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">
                {t('settings.whatsapp.statusLabel')}
              </dt>
              <dd>
                {health.isLoading || health.isFetching ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Loader2 size={12} className="animate-spin" />
                    {t('settings.whatsapp.statusChecking')}
                  </span>
                ) : health.isError ? (
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('settings.whatsapp.statusCheckFailed')}
                  </span>
                ) : health.data?.healthy ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold"
                    style={{ color: WHATSAPP_GREEN }}
                  >
                    <CheckCircle2 size={13} />
                    {t('settings.whatsapp.statusHealthy')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-destructive">
                    <XCircle size={13} />
                    {t('settings.whatsapp.statusUnhealthy')}
                  </span>
                )}
              </dd>
            </div>
          </dl>

          {!health.isLoading &&
            !health.isFetching &&
            !health.isError &&
            health.data &&
            !health.data.healthy && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-xs text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {health.data.errorMessage || t('settings.whatsapp.statusUnhealthy')}
                  {health.data.errorCode ? ` (${health.data.errorCode})` : ''}
                </span>
              </div>
            )}
          <div className="flex justify-between items-center">
            <button
              type="button"
              className="ow-btn ow-btn--outline ow-btn--sm"
              onClick={() => health.refetch()}
              disabled={health.isFetching}
            >
              <RefreshCw
                size={14}
                className={health.isFetching ? 'animate-spin' : ''}
              />
              {health.isFetching
                ? t('settings.whatsapp.checking')
                : t('settings.whatsapp.verify')}
            </button>
            <button
              type="button"
              className="ow-btn ow-btn--outline ow-btn--sm"
              onClick={() => setConfirmOpen(true)}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending && (
                <Loader2 size={14} className="animate-spin" />
              )}
              {disconnectMutation.isPending
                ? t('settings.whatsapp.disconnecting')
                : t('settings.whatsapp.disconnect')}
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.whatsapp.disconnectConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.whatsapp.disconnectConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisconnect}>
              {t('settings.whatsapp.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
